import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import SelectInput from "../common/SelectInput";
import { searchTaxonomy, getTaxonomyById } from "../../api/taxonomyApi";
import { TaxonomyResponseDto } from "../common/ObjectTypes";

// Wildlife Insights "Unknown" taxon — API settings.WI_UNKNOWN_TAXON_GUID.
// Unmapped predictions resolve to this taxon, so it is offered as a selectable
// filter option for "predictions with no mapping".
const WI_UNKNOWN_GUID = "822f8b93-f963-40cd-afe1-932f3ad5c1f2";

type TaxonOption = { value: number; label: string };

type TaxonMultiSelectProps = {
  /** Currently selected taxon ids (predicted_taxon_ids). */
  value: number[];
  /** Called with the new set of selected taxon ids. */
  onChange: (ids: number[]) => void;
};

/**
 * Build a human label for a taxonomy row: common name, falling back to
 * scientific name, then the id. Taxon names are data and are not translated.
 * @param tax the taxonomy row
 * @returns a display label
 */
const taxonLabel = (tax: TaxonomyResponseDto): string =>
  tax.common_name_english || tax.scientific_name || tax.species || `#${tax.id}`;

/**
 * Multi-select taxon picker for the species filter — matches AI predictions
 * and confirmed/edited observations alike (AKL-734), not just AI suggestions,
 * so it's labeled "Species" rather than "AI Suggested Species". Type-ahead
 * over the global taxonomy (`/taxonomy/search`); the WI-Unknown taxon is
 * always offered so reviewers can filter for unmapped/fallback predictions.
 * Emits the selected taxonomy ids (which equal the API's `predicted_taxon_ids`).
 */
const TaxonMultiSelect: React.FC<TaxonMultiSelectProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  // Cache id -> label so selected chips render even after the search box clears.
  const labelCache = useRef<Map<number, string>>(new Map());
  const [wiUnknown, setWiUnknown] = useState<TaxonOption | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadWiUnknown = async () => {
      try {
        const tax = await getTaxonomyById(WI_UNKNOWN_GUID);
        if (cancelled || !tax) return;
        const option = { value: tax.id, label: t("Unknown (unmapped)") };
        labelCache.current.set(tax.id, option.label);
        setWiUnknown(option);
      } catch (error) {
        console.error("Error resolving WI-Unknown taxon:", error);
      }
    };
    loadWiUnknown();
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Debounced taxonomy search. Stable across renders so the timer persists
  // between keystrokes; cancelled on unmount below. The WI-Unknown option is
  // prepended in loadOptions (reading the latest state) rather than here.
  const debouncedSearch = useMemo(
    () =>
      debounce(
        (inputValue: string, callback: (options: TaxonOption[]) => void) => {
          searchTaxonomy(inputValue)
            .then((results) => {
              const options = results.map((tax) => {
                const label = taxonLabel(tax);
                labelCache.current.set(tax.id, label);
                return { value: tax.id, label };
              });
              callback(options);
            })
            .catch((error) => {
              console.error("Error searching taxonomy:", error);
              callback([]);
            });
        },
        300,
      ),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const loadOptions = (
    inputValue: string,
    callback: (options: TaxonOption[]) => void,
  ) => {
    const withWiUnknown = (options: TaxonOption[]) =>
      wiUnknown ? [wiUnknown, ...options] : options;
    const term = inputValue.trim();
    if (term.length < 3) {
      debouncedSearch.cancel();
      callback(withWiUnknown([]));
      return;
    }
    debouncedSearch(term, (options) => callback(withWiUnknown(options)));
  };

  const selectedOptions: TaxonOption[] = value.map((id) => ({
    value: id,
    label: labelCache.current.get(id) ?? `#${id}`,
  }));

  return (
    <SelectInput
      options={[]}
      isAsync
      loadOptions={loadOptions}
      className="w-full bg-storm-blue rounded shadow text-left text-sm"
      isDisabled={false}
      isLoading={false}
      isClearable={true}
      isSearchable={true}
      isMulti={true}
      name={t("Species")}
      value={selectedOptions}
      onChange={(selected: TaxonOption[] | null) =>
        onChange((selected ?? []).map((o) => o.value))
      }
      errors={""}
      searchBar={false}
      placeholder={t("Type to search species...")}
      noOptionsMessage={() => t("Type at least 3 characters to search")}
    />
  );
};

export default TaxonMultiSelect;
